package com.islamicmedia.site.components

import androidx.compose.runtime.*
import com.islamicmedia.shared.domain.model.concernDisplayName
import com.islamicmedia.shared.presentation.ProgressStatus
import com.islamicmedia.shared.presentation.ProgressStep
import com.islamicmedia.site.components.FlappyCrescentGame
import com.islamicmedia.site.components.FitrahRunnerGame
import com.islamicmedia.site.components.DhikrCounter
import com.islamicmedia.site.theme.*
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

private val genericStepLabels = listOf(
    "Searching the web...",
    "Gathering content reviews...",
    "Checking parental guides...",
    "Reading community feedback...",
    "Judging problematic themes...",
    "Evaluating Islamic concerns...",
    "Assessing age-appropriateness...",
    "Compiling final analysis..."
)

private fun genericLabel(stepNumber: Int, totalSteps: Int, query: String): String {
    // Keep the analyzing step's own label
    if (query.contains("Analyzing", ignoreCase = true)) return query
    val idx = (stepNumber - 1).coerceIn(0, genericStepLabels.lastIndex)
    return genericStepLabels[idx]
}

@Composable
fun ProgressTracker(
    steps: List<ProgressStep>,
    progress: Float,
    currentCost: Double,
    title: String? = null,
    /** Author line — shown as "Author • year" under the title for BOOKS (matches strip/detail). */
    author: String? = null,
    mediaType: com.islamicmedia.shared.domain.model.MediaType? = null,
    quickCheck: Boolean = false,
    /** Cache hit — the result lands in a second or two, so the "how long this takes" line is wrong. */
    cacheHit: Boolean = false,
    /** Poster shown above the title while analyzing. Null → placeholder. */
    coverUrl: String? = null,
    /** Dev-only: epoch-millis when the analysis started. Drives the live wall-clock timer. */
    analysisStartedAtMs: Long? = null,
    /** When non-null, renders a "← Search another title" button that returns to the search view
     *  while keeping this analysis running in the background. */
    onSearchMore: (() -> Unit)? = null,
    /** True once the analysis has finished (or Phase 1 has landed) — the caller would normally be
     *  showing results now. Drives the "ready" banner over an open activity instead of yanking the
     *  activity away out from under the user. */
    analysisReady: Boolean = false,
    /** Opens the analysis. Called from the ready banner (body or "View" button). */
    onViewAnalysis: () -> Unit = {},
    /** Reports true while an activity is open, false once it closes (and on dispose), so the
     *  caller can hold this view on screen instead of switching to results underneath the user. */
    onActivityOpenChanged: (Boolean) -> Unit = {},
) {
    // Mirrors mobile: the cover is a HERO BANNER at the top of the card that fades into the white
    // surface, NOT the card's background. So the chrome below it is dark-on-white with no scrim,
    // no white-on-art retinting, and no text halos — only the pill floating over the hero needs
    // over-art treatment.
    val resolving = coverUrl.isNullOrBlank()
    val cMain = "rgb(14,124,123)"
    val cBody = "rgb(115,115,115)"
    val cFaint = "rgb(115,115,115)"
    val cChipText = "rgb(14,124,123)"
    val cTileBg = "transparent"
    val cTileBorder = "$Gray300"
    val cTrack = "#E5E5E5"
    // This box is opaque white and reaches every edge of the page card, so it MUST carry the card's
    // own radius and clip to it — otherwise its square corners paint white wedges over the card's
    // rounded ones. With radius + overflow:hidden here, the hero and the content below are both
    // clipped to the card shape and need no corner handling of their own.
    Div(attrs = {
        style {
            property("position", "relative")
            property("overflow", "hidden")
            property("border-radius", RadiusXLarge)
            property("background", "#FFFFFF")
        }
    }) {
        // ---------- Cover hero ----------
        // Square like mobile's default, capped so the progress bar and copy stay above the fold on
        // a short viewport. background-size: cover means a shorter hero just reveals less poster.
        Div(attrs = {
            style {
                property("position", "relative")
                width(100.percent)
                property("aspect-ratio", "1 / 1")
                property("max-height", "min(42vh, 380px)")
                property("overflow", "hidden")
                // Deep teal base so a slow/failed cover never flashes white before the art lands.
                property("background", "#0B3F3E")
            }
        }) {
            // Pre-cover layer: lighter green→white→gold gradient with two drifting
            // orbs. Crossfades out when the cover loads (opacity transition).
            Div(attrs = {
                style {
                    property("position", "absolute")
                    property("inset", "0")
                    property("z-index", "0")
                    property("background", "linear-gradient(135deg, #F8F8F4 0%, #FFFFFF 50%, #FAF5EA 100%)")
                    property("opacity", if (resolving) "1" else "0")
                    property("transition", "opacity 400ms ease")
                }
            }) {
                // Teal orb walks the hero perimeter (top→right→bottom→left, loop).
                // Centered on the position via translate(-50%, -50%).
                Div(attrs = {
                    style {
                        property("position", "absolute")
                        property("width", "900px")
                        property("height", "900px")
                        property("border-radius", "50%")
                        property("transform", "translate(-50%, -50%)")
                        property("background", "radial-gradient(circle, rgba(20,163,160,0.3), rgba(20,163,160,0))")
                        property("filter", "blur(8px)")
                        property("animation", "orb1Perimeter 8s linear infinite, orbPulse 0.7s ease-in-out infinite alternate")
                    }
                }) {}
                // Gold orb walks the perimeter 180° offset from teal.
                Div(attrs = {
                    style {
                        property("position", "absolute")
                        property("width", "860px")
                        property("height", "860px")
                        property("border-radius", "50%")
                        property("transform", "translate(-50%, -50%)")
                        property("background", "radial-gradient(circle, rgba(200,150,46,0.3), rgba(200,150,46,0))")
                        property("filter", "blur(8px)")
                        property("animation", "orb2Perimeter 8s linear infinite, orbPulse2 0.8s ease-in-out infinite alternate")
                    }
                }) {}
            }
            // Cover layer: the art itself, sharp — no scrim, since nothing sits on it but the pill.
            Div(attrs = {
                style {
                    property("position", "absolute")
                    property("inset", "0")
                    property("z-index", "0")
                    property("opacity", if (resolving) "0" else "1")
                    property("transition", "opacity 400ms ease")
                }
            }) {
                if (!coverUrl.isNullOrBlank()) {
                    Div(attrs = {
                        style {
                            property("position", "absolute")
                            property("inset", "0")
                            property("background-image", "url(" + (coverUrl ?: "") + ")")
                            property("background-size", "cover")
                            property("background-position", "center")
                            property("filter", "saturate(1.1)")
                        }
                    }) {}
                }
            }
            // Bottom fade — the hero dissolves into the white surface below it, so there's no hard
            // edge between the art and the content.
            Div(attrs = {
                style {
                    property("position", "absolute")
                    property("inset", "0")
                    property("z-index", "1")
                    property("background", "linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 55%, #FFFFFF 100%)")
                    property("pointer-events", "none")
                }
            }) {}
            // "Search another title" — keeps this task running, returns user to search. Floats over
            // the hero, so it keeps the over-art styling (translucent black + white text).
            if (onSearchMore != null) {
                Div(attrs = {
                    onClick { onSearchMore() }
                    style {
                        property("position", "absolute")
                        property("top", "12px")
                        property("left", "12px")
                        property("z-index", "2")
                        display(DisplayStyle.InlineBlock)
                        padding(8.px, 14.px)
                        borderRadius(999.px)
                        property("background", "rgba(0,0,0,0.35)")
                        property("border", "1px solid rgba(255,255,255,0.45)")
                        cursor("pointer")
                        fontSize(13.px)
                        fontWeight(600)
                        property("color", "#FFFFFF")
                        property("text-shadow", "0 1px 5px rgba(0,0,0,0.75)")
                        property("transition", "background 0.15s ease")
                    }
                }) { Text("← Search another title") }
            }
        }

        // ---------- Title + analyzing chrome (below the hero, on the white card) ----------
        Div(attrs = {
            style {
                property("position", "relative")
                property("z-index", "1")
                padding(14.px, 20.px, 20.px, 20.px)
            }
        }) {
        // Pull a trailing "(YYYY)" off the title so the year can join the author line below —
        // mirrors the strip cards and the detail view.
        val yearMatch = title?.let { Regex("""\s*\((\d{4})\)\s*$""").find(it) }
        val bareTitle = if (title != null && yearMatch != null) title.removeRange(yearMatch.range).trim() else title
        val meta = listOfNotNull(
            author?.takeIf { mediaType == com.islamicmedia.shared.domain.model.MediaType.BOOKS && it.isNotBlank() },
            yearMatch?.groupValues?.get(1)
        ).joinToString(" • ")
        // Title in brand teal on the white surface, directly under the hero (mirrors mobile).
        Div(attrs = {
            style {
                property("font-family", TitleFontFamily)
                fontSize(22.px)
                fontWeight(700)
                marginBottom(if (meta.isNotEmpty()) 2.px else 4.px)
                property("color", cMain)
            }
        }) { Text(bareTitle ?: "Analysis in progress") }
        // Author (books) + year, joined by a dot — same meta line as every other view.
        if (title != null && meta.isNotEmpty()) {
            Div(attrs = {
                style {
                    fontSize(13.px)
                    fontWeight(500)
                    marginBottom(4.px)
                    property("color", cBody)
                }
            }) { Text(meta) }
        }
        // Animated dots + live step label (what the old "Researching…" pill showed) — now the
        // subtitle under the title. Reflects the current stage with animated trailing dots.
        var dotCount by remember { mutableStateOf(1) }
        LaunchedEffect(Unit) {
            while (true) { delay(500); dotCount = (dotCount % 3) + 1 }
        }
        val dots = ".".repeat(dotCount)
        val activeQuery = (steps.lastOrNull { it.status == ProgressStatus.SEARCHING }?.query
            ?: steps.lastOrNull { it.status == ProgressStatus.COMPLETED }?.query
            ?: "").lowercase()
        val currentStepBase: String = when {
            activeQuery.contains("age-appropriate") || activeQuery.contains("guidance") -> "Adding developmental guidance"
            activeQuery.contains("compiling") -> "Compiling report"
            activeQuery.contains("cross-referencing") -> "Cross-referencing findings"
            activeQuery.contains("checking") || activeQuery.contains("concern") -> {
                val concernMatch = Regex("concern \\d+/\\d+:\\s*(.+)", RegexOption.IGNORE_CASE).find(activeQuery)
                if (concernMatch != null) "Evaluating ${concernDisplayName(concernMatch.groupValues[1].trim().replaceFirstChar { it.uppercase() })}"
                else "Evaluating concerns"
            }
            activeQuery.contains("condensing") || activeQuery.contains("summariz") -> "Processing findings"
            activeQuery.contains("cache") -> "Extracting analysis"
            activeQuery.isNotEmpty() -> "Researching"
            else -> "Starting analysis"
        }
        if (title != null) {
            Div(attrs = {
                style {
                    marginBottom(16.px)
                    fontSize(14.px)
                    fontWeight(500)
                    property("color", cBody)
                }
            }) { Text("$currentStepBase$dots") }
        }

        // Dev-only: live wall-clock timer
        if (com.islamicmedia.shared.AppConfig.isDev && analysisStartedAtMs != null) {
            var elapsedMs by remember(analysisStartedAtMs) { mutableStateOf(0L) }
            LaunchedEffect(analysisStartedAtMs) {
                while (true) {
                    elapsedMs = com.islamicmedia.shared.util.currentTimeMillis() - analysisStartedAtMs
                    delay(250)
                }
            }
            Div(attrs = {
                style {
                    fontSize(12.px)
                    property("color", cFaint)
                    fontWeight(500)
                    marginTop((-12).px)
                    marginBottom(12.px)
                }
            }) { Text("⏱ ${com.islamicmedia.shared.util.formatDurationCompact(elapsedMs)}") }
        }

        val progressPct = (progress * 100f).toDouble()

        Div(attrs = {
            style {
                height(6.px)
                borderRadius(3.px)
                property("background", cTrack)
                property("overflow", "hidden")
                marginBottom(8.px)
            }
        }) {
            Div(attrs = {
                style {
                    width(progressPct.percent)
                    height(6.px)
                    borderRadius(3.px)
                    property("background", "linear-gradient(90deg, $Primary 0%, $Accent 100%)")
                    property("transition", "width 0.5s ease")
                }
            })
        }


        // Activity selector / game area while waiting
        var selectedActivity by remember { mutableStateOf<String?>(null) }

        // Tell the caller whether an activity is open, so it can keep this view on screen instead
        // of swapping to results mid-game. Also reported false on dispose so the caller can never
        // get stuck holding the flag if this composable goes away some other way.
        LaunchedEffect(selectedActivity) {
            onActivityOpenChanged(selectedActivity != null)
        }
        DisposableEffect(Unit) {
            onDispose { onActivityOpenChanged(false) }
        }

        // Dismissible "ready" banner shown over an open activity once the analysis is ready.
        // Resets whenever analysisReady goes back to false (a new task, or this one un-readying).
        var readyBannerDismissed by remember { mutableStateOf(false) }
        LaunchedEffect(analysisReady) {
            if (!analysisReady) readyBannerDismissed = false
        }

        Div(attrs = {
            style {
                marginBottom(16.px)
            }
        }) {
            if (selectedActivity == null) {
                // Selector with bordered text buttons
                Div(attrs = {
                    style {
                        property("text-align", "center")
                        marginTop(24.px)
                    }
                }) {
                    Span(attrs = {
                        style {
                            fontSize(14.px)
                            property("color", cBody)
                            fontWeight(400)
                            lineHeight(1.6.em)
                            display(DisplayStyle.Block)
                            property("text-align", "center")
                            marginBottom(16.px)
                        }
                    }) {
                        Text(
                            if (quickCheck)
                                "We're checking trusted sources for concerns that matter to your family."
                            else
                                "We're consulting Islamic scholarship, parental review databases, and community insights to build a thorough, faith-informed analysis for your family."
                        )
                    }

                    Span(attrs = {
                        style {
                            fontSize(14.px)
                            property("color", cBody)
                            fontWeight(400)
                            display(DisplayStyle.Block)
                            marginBottom(16.px)
                        }
                    }) { Text("While you wait, try one of these activities...") }

                    Div(attrs = {
                        style {
                            display(DisplayStyle.Flex)
                            gap(12.px)
                            property("justify-content", "center")
                            padding(0.px, 16.px)
                        }
                    }) {
                        Div(attrs = {
                            style {
                                property("border", "1px solid $cTileBorder")
                                property("background", cTileBg)
                                borderRadius(12.px)
                                padding(12.px, 16.px)
                                property("cursor", "pointer")
                                property("flex", "1")
                                property("text-align", "center")
                            }
                            onClick { selectedActivity = "runner" }
                        }) {
                            Span(attrs = {
                                style {
                                    fontSize(14.px)
                                    fontWeight(600)
                                    property("color", cChipText)
                                }
                            }) { Text("Stay on the Fitrah") }
                        }
                        Div(attrs = {
                            style {
                                property("border", "1px solid $cTileBorder")
                                property("background", cTileBg)
                                borderRadius(12.px)
                                padding(12.px, 16.px)
                                property("cursor", "pointer")
                                property("flex", "1")
                                property("text-align", "center")
                            }
                            onClick { selectedActivity = "dhikr" }
                        }) {
                            Span(attrs = {
                                style {
                                    fontSize(14.px)
                                    fontWeight(600)
                                    property("color", cChipText)
                                }
                            }) { Text("Get Your Dhikr On") }
                        }
                    }
                }
            } else {
                // Back link
                Div(attrs = {
                    style {
                        marginBottom(8.px)
                    }
                }) {
                    Span(attrs = {
                        style {
                            fontSize(13.px)
                            property("color", cChipText)
                            fontWeight(600)
                            property("cursor", "pointer")
                        }
                        onClick { selectedActivity = null }
                    }) { Text("\u2190 Back to activities") }
                }

                // "Your analysis is ready" banner \u2014 only while an activity is open, only once the
                // analysis would otherwise have replaced this view, and only until dismissed.
                if (analysisReady && !readyBannerDismissed) {
                    Div(attrs = {
                        onClick { onViewAnalysis() }
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.SpaceBetween)
                            gap(10.px)
                            marginBottom(10.px)
                            padding(10.px, 14.px)
                            borderRadius(12.px)
                            property("background", "rgba(14,124,123,0.08)")
                            property("border", "1px solid rgba(14,124,123,0.25)")
                            property("cursor", "pointer")
                        }
                    }) {
                        Span(attrs = {
                            style {
                                fontSize(13.px)
                                fontWeight(600)
                                property("color", cMain)
                            }
                        }) { Text("Your analysis is ready") }

                        Div(attrs = {
                            style {
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                gap(10.px)
                                property("flex-shrink", "0")
                            }
                        }) {
                            Span(attrs = {
                                onClick { it.stopPropagation(); onViewAnalysis() }
                                style {
                                    fontSize(13.px)
                                    fontWeight(700)
                                    property("color", "#FFFFFF")
                                    property("background", cMain)
                                    padding(4.px, 12.px)
                                    borderRadius(999.px)
                                    property("cursor", "pointer")
                                }
                            }) { Text("View") }

                            Span(attrs = {
                                onClick { it.stopPropagation(); readyBannerDismissed = true }
                                style {
                                    fontSize(14.px)
                                    fontWeight(600)
                                    property("color", cBody)
                                    property("cursor", "pointer")
                                    padding(2.px, 4.px)
                                }
                            }) { Text("\u2715") }
                        }
                    }
                }

                // Selected activity
                when (selectedActivity) {
                    "flappy" -> FlappyCrescentGame()
                    "runner" -> FitrahRunnerGame()
                    "dhikr" -> DhikrCounter()
                }
            }
        }

        // Rolodex card stack
        val activeIndex = steps.indexOfFirst { it.status == ProgressStatus.SEARCHING }
            .takeIf { it >= 0 } ?: (steps.size - 1).coerceAtLeast(0)

        // Status widget removed — games/activities fill the space instead

        }
    }

    // Inject animations
    InjectOrbKeyframes()
    InjectStyles(
        "progress-tracker-styles",
        """
        @keyframes pulse-glow {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.6; transform: scale(1.1); }
        }
        @keyframes ffPulse {
            from { opacity: 0.55; }
            to { opacity: 1; }
        }
        /* The chrome no longer retints when the cover lands (it sits on white, not on the art),
           so the old .analyzing-content / .cover-text rules are gone with it. */
        @keyframes timer-shake {
            0%, 100% { transform: translateX(0); }
            20% { transform: translateX(-3px); }
            40% { transform: translateX(3px); }
            60% { transform: translateX(-2px); }
            80% { transform: translateX(2px); }
        }
        .timer-shake {
            animation: timer-shake 0.5s ease-in-out infinite;
        }
        """.trimIndent()
    )
}

@Composable
private fun StackedCard(
    step: ProgressStep,
    isActive: Boolean,
    stackDepth: Int
) {
    val opacity = if (isActive) 1.0 else maxOf(0.4, 1.0 - stackDepth * 0.2)

    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(14.px)
            property("background", "rgba(255,255,255,0.55)")
            borderRadius(RadiusLarge.unsafeCast<CSSNumeric>())
            padding(16.px, 20.px)
            property("box-shadow", if (isActive) "0 4px 20px rgba(0,0,0,0.12)" else "0 2px 8px rgba(0,0,0,0.06)")
            property("border", if (isActive) "2px solid $PrimaryLight" else "1px solid $Gray200")
            property("opacity", "$opacity")
        }
    }) {
        // Status icon
        when (step.status) {
            ProgressStatus.SEARCHING -> {
                LoadingDots(size = 24)
            }
            ProgressStatus.COMPLETED -> {
                Img(src = "/images/check_done.png", attrs = {
                    style {
                        width(20.px)
                        height(20.px)
                        property("flex-shrink", "0")
                    }
                })
            }
            ProgressStatus.ERROR -> {
                Span(attrs = {
                    style {
                        fontSize(18.px)
                        width(24.px)
                        property("text-align", "center")
                        property("flex-shrink", "0")
                    }
                }) { Text("\u274C") }
            }
        }

        // Query text
        Span(attrs = {
            style {
                property("flex", "1")
                fontSize(14.px)
                color(if (isActive) Gray900 else Gray500)
                fontWeight(if (isActive) 600 else 400)
                property("white-space", "nowrap")
                property("overflow", "hidden")
                property("text-overflow", "ellipsis")
            }
        }) { Text(genericLabel(step.stepNumber, 0, step.query)) }

        // Duration badge for completed items
        if (step.status == ProgressStatus.COMPLETED && step.durationMs > 0 && isActive) {
            Span(attrs = {
                style {
                    backgroundColor(ScoreGreenBg)
                    color(ScoreGreen)
                    borderRadius(RadiusFull.unsafeCast<CSSNumeric>())
                    padding(4.px, 10.px)
                    fontSize(12.px)
                    fontWeight(600)
                    property("white-space", "nowrap")
                }
            }) { Text("${step.durationMs / 1000}s") }
        }
    }
}

@Composable
private fun ActivityCard(
    iconStyle: String,
    title: String,
    subtitle: String,
    onClick: () -> Unit
) {
    var hovered by remember { mutableStateOf(false) }

    val hasBackground = iconStyle == "runner" || iconStyle == "dhikr"
    val bgImage = when (iconStyle) {
        "runner" -> "/images/ic_runner_game.png"
        "dhikr" -> "/images/ic_dhikr_game.png"
        else -> null
    }

    Div(attrs = {
        style {
            property("flex", "1")
            property("max-width", "160px")
            height(120.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            property("justify-content", "center")
            gap(4.px)
            borderRadius(RadiusLarge.unsafeCast<CSSNumeric>())
            property("overflow", "hidden")
            position(Position.Relative)
            if (bgImage != null) {
                property("background-image", "url('$bgImage')")
                property("background-size", "cover")
                property("background-position", "center")
            } else {
                property("background", "rgba(255,255,255,0.55)")
            }
            property("border", if (hovered) "2px solid #0E7C7B" else "1.5px solid $Gray200")
            property("cursor", "pointer")
            property("transition", "border-color 0.2s ease, box-shadow 0.2s ease")
            if (hovered) {
                property("box-shadow", "0 2px 12px rgba(14, 124, 123, 0.15)")
            }
        }
        onClick { onClick() }
        onMouseOver { hovered = true }
        onMouseOut { hovered = false }
    }) {
        // Title & subtitle centered over the card
        Div(attrs = {
            style {
                if (hasBackground) {
                    position(Position.Absolute)
                    left(0.px)
                    right(0.px)
                    top(0.px)
                    bottom(0.px)
                }
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Center)
                property("justify-content", "center")
                property("text-align", "center")
            }
        }) {
            val textShadow = if (hasBackground) "0 0 3px white, 0 0 3px white" else "none"
            Span(attrs = {
                style {
                    fontSize(13.px)
                    fontWeight(700)
                    color(Color("#0E7C7B"))
                    property("line-height", "1.2")
                    property("text-shadow", textShadow)
                }
            }) { Text(title) }

            Span(attrs = {
                style {
                    fontSize(11.px)
                    fontWeight(600)
                    color(Gray500)
                    property("text-shadow", textShadow)
                }
            }) { Text(subtitle) }
        }
    }
}
